import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import type { RootState, AppDispatch } from "../../../../app/store";
import BillingAddressForm from "./BillingAddressForm";
import {
  useGetCartQuery,
  useUpdateCustomerMutation,
} from "../../../cart/api/cartApi";
import {
  completeStep,
  setIsCurrentStepFormValid,
  setUseBillingForShipping,
} from "../../checkoutSlice";
import RTKQErrorCard from "../../../../common/components/RTKQErrorCard";
import type { RTKQueryError } from "../../../../common/components/RTKQErrorCard";
import type { TransformedAddress } from "../../../cart/types/cartApiTypes";
import type { BillingAddressFormProps } from "./BillingAddressForm";
import type { TrCity, TrCityWithDistricts } from "../../types/checkoutApiTypes";

interface BillingStepContainerProps {
  onComplete: () => void;
  cities?: TrCity[]; // Add cities prop (optional for initial render)
  citiesWithDistricts?: TrCityWithDistricts[]; // Add districts prop
}

// Helper function
const mapTransformedToFormData = (
  address?: Partial<TransformedAddress>
): Partial<BillingAddressFormProps["initialData"]> => {
  if (!address) return {};
  return {
    firstName: address.firstName || "",
    lastName: address.lastName || "",
    company: address.company || "",
    address1: address.address1 || "",
    address2: address.address2 || "",
    city: address.city || "",
    state: address.state || "", // Keep the state code here initially
    postcode: address.postcode || "",
    country: address.country || "",
    email: address.email || "",
    phone: address.phone || "",
  };
};

const BillingStepContainer: React.FC<BillingStepContainerProps> = ({
  onComplete,
  cities, // Receive cities prop
  citiesWithDistricts, // Receive districts prop
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const formId = "billing-address-form";
  const currentStep = useSelector(
    (state: RootState) => state.checkout.currentStep
  );
  const useBillingForShipping = useSelector(
    (state: RootState) => state.checkout.useBillingForShipping
  );
  const { data: cartData, isLoading: isCartLoading } = useGetCartQuery();
  const initialBillingData = mapTransformedToFormData(cartData?.billingAddress);
  const [
    updateCustomer,
    { isLoading: isUpdating, isError, error: updateError },
  ] = useUpdateCustomerMutation();

  const handleApiSubmit = async (apiData: Partial<TransformedAddress>) => {
    console.log("Submitting Billing Address via API:", apiData);
    try {
      await updateCustomer({ billingAddress: apiData }).unwrap();
      dispatch(completeStep("billing"));
      onComplete();
    } catch (err) {
      console.error("Failed to update billing address:", err);
    }
  };

  const handleValidityChange = useCallback(
    (isValid: boolean) => {
      if (currentStep === "billing") {
        dispatch(setIsCurrentStepFormValid(isValid));
      }
    },
    [dispatch, currentStep]
  );

  const handleToggleUseBilling = useCallback(
    (isChecked: boolean) => {
      dispatch(setUseBillingForShipping(isChecked));
    },
    [dispatch]
  );

  if (isCartLoading) {
    return (
      <div className="billing-step-container skeleton-billing">
        <h2>
          <Skeleton width={200} height={24} />
        </h2>
        <div className="form-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`form-field ${i > 1 ? "full-width" : ""}`}>
              <Skeleton height={16} width="40%" style={{ marginBottom: 5 }} />
              <Skeleton height={44} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="billing-step-container">
      <h2>{t("checkout.billingAddress", "Billing Address")}</h2>
      {isError && (
        <RTKQErrorCard
          error={updateError as RTKQueryError | null}
          title={t("checkout.errors.updateFailed", "Failed to update address")}
          compact
        />
      )}
      <BillingAddressForm
        formId={formId}
        onSubmit={handleApiSubmit}
        initialData={initialBillingData}
        isSubmitting={isUpdating}
        currentStep={currentStep}
        useBillingForShipping={useBillingForShipping}
        onValidityChange={handleValidityChange}
        onToggleUseBilling={handleToggleUseBilling}
        cities={cities}
        citiesWithDistricts={citiesWithDistricts} // Pass districts down
      />
    </div>
  );
};

export default BillingStepContainer;
